import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { ActionIcon, Box, Button, Flex, TableProps, Tooltip } from '@mantine/core';
import { DateTime } from 'luxon';
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_Row,
  useMantineReactTable,
} from 'mantine-react-table';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/Icon/Icon';
import ManifestModal from '../../../components/ManifestModal/ManifestModal';
import { getDefaultTableOptions } from '../../../components/MRT/ManifestMRT';
import { PopoverLabel } from '../../../components/PopoverLabel/PopoverLabel';
import { useDeleteOrganizationUser } from '../../../hooks/mutations/useDeleteOrganizationUser';
import { usePostOrganizationUser } from '../../../hooks/mutations/user/usePostOrganizationUser';
import { usePutOrganizationUser } from '../../../hooks/mutations/user/usePutOrganizationUser';
import { useFetchOrganization } from '../../../hooks/queries/useFetchOrganization';
import {
  MappedOrganizationMember,
  useFetchOrganizationUsers,
} from '../../../hooks/queries/useFetchOrganizationUsers';
import { ManifestNotifications } from '../../../hooks/utils/useNotifications';
import { useOrganizationId } from '../../../hooks/utils/useOrganizationId';
import { MemberModal } from './MemberModal';

const defaultTableOptions = getDefaultTableOptions<MappedOrganizationMember>();

interface Props {
  hasEditPermissions: boolean;
}

export const PeopleTable = ({ hasEditPermissions }: Props) => {
  const { t } = useTranslation();
  const [currentOrgId] = useOrganizationId();
  const [selectedUserToDelete, setSelectedUserToDelete] = useState<
    MappedOrganizationMember | undefined
  >();
  const [openedModal, setOpenedModal] = useState<
    { mode: 'create' | 'edit'; selectedUser?: MappedOrganizationMember } | undefined
  >();
  const { mutateAsync: postUser, isLoading: isCreatingOrganizationUser } =
    usePostOrganizationUser();
  const { data: currentOrganization } = useFetchOrganization({
    organizationId: currentOrgId,
  });
  const {
    data: fetchedOrganizationUsers = [],
    isLoading: isLoadingOrganizationUsers,
    isFetching: isFetchingOrganizationUsers,
  } = useFetchOrganizationUsers({
    options: { refetchOnWindowFocus: false },
  });
  const { mutateAsync: putOrganizationUser, isLoading: isUpdatingOrganizationUser } =
    usePutOrganizationUser();
  const { mutateAsync: deleteOrganizationUser, isLoading: isDeletingOrganizationUser } =
    useDeleteOrganizationUser();

  const columns = useMemo<MRT_ColumnDef<MappedOrganizationMember>[]>(
    () => [
      {
        accessorKey: 'firstName',
        header: t('page.userSettings.organization.people.table.headers.firstName'),
        size: 120,
      },
      {
        accessorKey: 'lastName',
        header: t('page.userSettings.organization.people.table.headers.lastName'),
        size: 120,
      },
      {
        accessorKey: 'email',
        header: t('page.userSettings.organization.people.table.headers.email'),
        enableEditing: false,
      },
      {
        accessorKey: 'title',
        header: t('page.userSettings.organization.people.table.headers.title'),
        size: 120,
      },
      {
        accessorKey: 'roles',
        header: t('page.userSettings.organization.people.table.headers.roles'),
        size: 120,
        Cell: ({ row }: { row: MRT_Row<MappedOrganizationMember> }) => {
          const [firstRole, ...otherRoles] = row.original.roles || [];

          return (
            <Flex>
              <Box mr="4px">{firstRole?.name}</Box>
              {otherRoles.length > 0 && (
                <PopoverLabel label={`+(${otherRoles.length})`}>
                  {otherRoles.map((role) => (
                    <TextComponent key={role.id}>{role.name}</TextComponent>
                  ))}
                </PopoverLabel>
              )}
            </Flex>
          );
        },
        enableColumnFilter: false,
      },
      {
        accessorFn: (row) => new Date(row.lastLogin),
        header: t('page.userSettings.organization.people.table.headers.lastLogin'),
        enableColumnFilter: false,
        enableEditing: false,
        Cell: ({ cell }) => {
          const cellValue = cell.getValue<string>();
          let dateObject = DateTime.fromISO(cellValue);

          if (!dateObject.isValid) {
            dateObject = DateTime.fromJSDate(new Date(cellValue));
          }

          if (dateObject.isValid) {
            const date = dateObject.toLocaleString(DateTime.DATETIME_MED);
            return (
              <Tooltip label={dateObject.toRelative()}>
                <span>{date}</span>
              </Tooltip>
            );
          } else {
            return <span>{t('global.invalidDate')}</span>;
          }
        },
        size: 120,
      },
    ],
    [],
  );

  const table = useMantineReactTable<MappedOrganizationMember>({
    ...defaultTableOptions,
    columns,
    data: fetchedOrganizationUsers,
    getRowId: (row) => row.memberId,
    enableFacetedValues: true,
    enableEditing: hasEditPermissions,
    enableRowActions: hasEditPermissions,
    initialState: {
      ...defaultTableOptions.initialState,
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      sorting: [{ id: 'email', desc: false }],
    },
    state: {
      isLoading: isLoadingOrganizationUsers,
      isSaving: isUpdatingOrganizationUser,
      showLoadingOverlay:
        isFetchingOrganizationUsers ||
        isUpdatingOrganizationUser ||
        isDeletingOrganizationUser ||
        isCreatingOrganizationUser,
    },
    ...(hasEditPermissions && {
      renderTopToolbarCustomActions: () => (
        <Button
          leftIcon={<Icon icon="plus" />}
          onClick={() => setOpenedModal({ mode: 'create' })}
        >
          {t('page.userSettings.organization.people.add-user')}
        </Button>
      ),
      renderRowActions: ({ row }) => (
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Tooltip label={t('global.edit')}>
            <ActionIcon
              color="gray"
              onClick={() => {
                setOpenedModal({ mode: 'edit', selectedUser: row.original });
              }}
              data-testid="edit-user-table-action"
              aria-label={`${t('global.edit')} ${row.original.firstName} ${row.original.lastName}`}
            >
              <Icon icon="edit" />
            </ActionIcon>
          </Tooltip>
          <Tooltip label={t('global.delete')}>
            <ActionIcon
              color="gray"
              aria-label={`${t('global.delete')} ${row.original.firstName} ${row.original.lastName}`}
              data-testid="delete-user-table-action"
              onClick={() => setSelectedUserToDelete(row.original)}
            >
              <Icon icon="trash" />
            </ActionIcon>
          </Tooltip>
        </Box>
      ),
      mantineTableProps: { 'data-testid': `people-table` } as TableProps,
    }),
  });

  return (
    <>
      <MantineReactTable table={table} />
      {openedModal && (
        <MemberModal
          subOrganizationIds={currentOrganization?.children ?? []}
          title={
            openedModal.mode === 'create'
              ? t('page.userSettings.organization.people.header-create')
              : t('page.userSettings.organization.people.table.headers.edit-user')
          }
          mode={openedModal.mode}
          isLoading={isCreatingOrganizationUser || isUpdatingOrganizationUser}
          user={openedModal.selectedUser}
          defaultValues={
            openedModal.selectedUser && {
              firstName: openedModal.selectedUser?.firstName,
              lastName: openedModal.selectedUser?.lastName,
              title: openedModal.selectedUser?.title,
              email: openedModal.selectedUser?.email,
              roles: openedModal.selectedUser?.roles || [],
            }
          }
          opened
          onClose={() => setOpenedModal(undefined)}
          onConfirm={async (user) => {
            const suborganizationIds = currentOrganization?.children ?? [];
            const hasSubOrgs = suborganizationIds.length > 0;
            const isSelectedAdminRole = user.roles
              .map((role) => role.urn)
              .includes('admin');
            const isAdminOfSubOrgs = Boolean(
              openedModal.selectedUser?.adminOfSubOrgsIds?.length,
            );
            const isAdminOfAllExistingSubOrgs =
              isAdminOfSubOrgs &&
              suborganizationIds.every((subOrgId) =>
                openedModal.selectedUser?.adminOfSubOrgsIds?.includes(subOrgId),
              );
            const addAsAdminToAllSubOrgs =
              isSelectedAdminRole &&
              hasSubOrgs &&
              !isAdminOfAllExistingSubOrgs &&
              user.addAsAdminToAllSubOrgs === 'yes';

            const { roles, ...rest } = user;
            const roleUrns = roles.map((role) => role.urn);
            try {
              if (openedModal?.mode === 'create') {
                await postUser({
                  user: {
                    ...rest,
                    roleUrns,
                    addAsAdminToAllSubOrgs: addAsAdminToAllSubOrgs,
                  },
                });
              } else if (openedModal.selectedUser) {
                await putOrganizationUser({
                  user: {
                    ...rest,
                    roleUrns: roleUrns,
                    addAsAdminToAllSubOrgs: addAsAdminToAllSubOrgs,
                  },
                  memberId: openedModal.selectedUser.memberId,
                });
              }

              setOpenedModal(undefined);
            } catch {
              ManifestNotifications.showErrorNotification({
                title: t('global.unexpectedError'),
                message: t('global.unexpectedErrorSubtitle'),
              });
            }
          }}
          org={currentOrganization}
        />
      )}

      {selectedUserToDelete && (
        <ManifestModal
          dataTestId="delete-user-modal"
          title={t('page.userSettings.organization.people.confirmDeleteModal.title')}
          onClose={() => setSelectedUserToDelete(undefined)}
          opened
          primaryAction={{
            isLoading: isDeletingOrganizationUser,
            label: t('global.delete'),
            onClick: async () => {
              try {
                await deleteOrganizationUser({
                  memberId: selectedUserToDelete.memberId,
                });

                setSelectedUserToDelete(undefined);
              } catch {
                ManifestNotifications.showErrorNotification({
                  title: t('global.unexpectedError'),
                  message: t('global.unexpectedErrorSubtitle'),
                });
              }
            },
            color: 'red',
          }}
          secondaryAction={{
            label: t('global.cancel'),
            onClick: () => {
              setSelectedUserToDelete(undefined);
            },
          }}
        >
          {t('page.userSettings.organization.people.confirmDeleteModal.body', {
            email: selectedUserToDelete.email,
          })}
        </ManifestModal>
      )}
    </>
  );
};
